import { useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Pause, Play, RotateCcw, X } from 'lucide-react'
import { useContext } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import AASMActionControls from 'components/reusable/AASMActionControls'
import { capitalize } from 'utils'
import {
  getGetBrokersTaskWorkflowsIdQueryKey,
  usePostBrokersTaskWorkflowsIdAction,
} from 'utils/web_api'
import {
  type BrokersWorkflowAvailableActionsItem,
  type BrokersWorkflowSummary,
} from 'utils/web_api.schemas'
import { invalidateWorkflowQueries } from 'utils/workflowCacheUtils'

import { sortAvailableActions } from './WorkflowVisualIndicators'

// Workflow Action Controls Component
const WorkflowActionControls = ({
  workflow,
  onSuccess,
}: {
  workflow: BrokersWorkflowSummary
  onSuccess?: (updatedWorkflow: BrokersWorkflowSummary) => void
}) => {
  const { memberId } = useContext(BrokersMemberContext)
  const queryClient = useQueryClient()
  const mutation = usePostBrokersTaskWorkflowsIdAction()

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'start':
        return 'Start'
      case 'complete':
        return 'Complete'
      case 'cancel':
        return 'Cancel'
      case 'pause':
        return 'Pause'
      case 'resume':
        return 'Resume'
      case 'reopen':
        return 'Reopen'
      default:
        return capitalize(action)
    }
  }

  const getActionIcon = (action: string | undefined, isPending: boolean) => {
    if (isPending) return <Loader2 className='w-4 h-4 animate-spin' />

    switch (action) {
      case 'start':
      case 'resume':
        return <Play className='w-4 h-4' />
      case 'pause':
        return <Pause className='w-4 h-4' />
      case 'cancel':
        return <X className='w-4 h-4' />
      case 'complete':
        return <Check className='w-4 h-4' />
      case 'reopen':
        return <Play className='w-4 h-4' />
      case 'rollback':
        return <RotateCcw className='w-4 h-4' />
      default:
        return <Play className='w-4 h-4' />
    }
  }

  const handleExecuteAction = (action: BrokersWorkflowAvailableActionsItem) => {
    const requestData: any = {
      id: workflow.id,
      action,
    }

    // Add cancellation_reason for cancel action
    if (action === 'cancel') {
      const reason = prompt('Enter cancellation reason (optional):')
      // If user clicked Cancel in the prompt, abort the cancellation
      if (reason === null) {
        return
      }
      if (reason) {
        requestData.data = { cancellation_reason: reason }
      }
    }

    mutation.mutate(requestData, {
      onSuccess: (updatedWorkflow) => {
        // Update the individual workflow cache directly with returned data
        // This provides immediate UI feedback for the specific query variant
        queryClient.setQueryData(
          getGetBrokersTaskWorkflowsIdQueryKey(workflow.id),
          updatedWorkflow
        )

        // Invalidate all workflow-related queries using centralized utility
        invalidateWorkflowQueries(queryClient, {
          workflowId: workflow.id,
          enrollmentId: workflow.insurance_enrollment_id,
          memberId,
        })

        // Call the optional onSuccess callback
        onSuccess?.(updatedWorkflow)
      },
      onError: (error) => {
        console.error('Failed to execute workflow action:', error)
        alert('Failed to execute action. Please try again.')
      },
    })
  }

  return (
    <AASMActionControls<BrokersWorkflowAvailableActionsItem>
      availableActions={workflow.available_actions}
      sortActions={sortAvailableActions}
      getActionLabel={getActionLabel}
      getActionIcon={getActionIcon}
      onActionExecute={handleExecuteAction}
      isPending={mutation.isPending}
    />
  )
}

export default WorkflowActionControls
